import React, { createContext, useContext, useState, useEffect } from "react";
import customerAccessService from "../services/customerAccessService";
import { useAuth } from "./AuthContext";

const CustomerContext = createContext();

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [accessibleCustomers, setAccessibleCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load accessible customers when user logs in
  useEffect(() => {
    if (isAuthenticated && user) {
      loadAccessibleCustomers();

      // Try to restore selected customer from localStorage
      const savedCustomerId = localStorage.getItem("cia_selected_customer_id");
      if (savedCustomerId) {
        restoreSelectedCustomer(savedCustomerId);
      }
    } else {
      // Clear customer data when user logs out
      setSelectedCustomer(null);
      setAccessibleCustomers([]);
      localStorage.removeItem("cia_selected_customer_id");
    }
  }, [isAuthenticated, user]);

  /**
   * Load all customers accessible to the current user
   */
  const loadAccessibleCustomers = async () => {
    try {
      setLoading(true);
      setError(null);

      const customers = await customerAccessService.getAccessibleCustomers();
      setAccessibleCustomers(customers || []);
    } catch (err) {
      const errorMessage = err.message || "Failed to load customers";
      setError(errorMessage);
      console.error("Error loading customers:", errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get primary customer (for auto-redirect)
   */
  const getPrimaryCustomer = async () => {
    try {
      const customer = await customerAccessService.getPrimaryCustomer();
      return customer;
    } catch (err) {
      console.error("Error getting primary customer:", err);
      return null;
    }
  };

  /**
   * Select a customer and save to state + localStorage
   */
  const selectCustomer = (customer) => {
    if (!customer) {
      console.error("Cannot select null customer");
      return;
    }

    setSelectedCustomer(customer);
    localStorage.setItem("cia_selected_customer_id", customer.id);
  };

  /**
   * Switch to a different customer by ID
   */
  const switchCustomer = async (customerId) => {
    try {
      setLoading(true);
      setError(null);

      // Validate access first
      const hasAccess = await customerAccessService.validateCustomerAccess(
        customerId
      );

      if (!hasAccess) {
        throw new Error("You don't have access to this customer");
      }

      // Find customer in accessible list or fetch it
      let customer = accessibleCustomers.find((c) => c.id === customerId);

      if (!customer) {
        // Fetch customer details if not in list
        customer = await customerAccessService.getCustomerById(customerId);
      }

      selectCustomer(customer);
    } catch (err) {
      const errorMessage = err.message || "Failed to switch customer";
      setError(errorMessage);
      console.error("Error switching customer:", errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Restore selected customer from localStorage
   */
  const restoreSelectedCustomer = async (customerId) => {
    try {
      // Validate access first
      const hasAccess = await customerAccessService.validateCustomerAccess(
        customerId
      );

      if (!hasAccess) {
        console.warn("No access to saved customer, clearing...");
        localStorage.removeItem("cia_selected_customer_id");
        return;
      }

      // Fetch customer details
      const customer = await customerAccessService.getCustomerById(customerId);
      setSelectedCustomer(customer);
    } catch (err) {
      console.error("Error restoring customer:", err);
      localStorage.removeItem("cia_selected_customer_id");
    }
  };

  /**
   * Validate if user has access to a customer
   */
  const validateAccess = async (customerId) => {
    return await customerAccessService.validateCustomerAccess(customerId);
  };

  /**
   * Add a newly created customer to accessible customers list
   * (Used after creating a customer to avoid re-fetching)
   */
  const addAccessibleCustomer = (customer) => {
    if (!customer) {
      console.error("Cannot add null customer");
      return;
    }

    setAccessibleCustomers((prev) => {
      // Check if customer already exists (avoid duplicates)
      const exists = prev.some((c) => c.id === customer.id);
      if (exists) {
        console.warn("Customer already in list:", customer.name);
        return prev;
      }

      return [...prev, customer];
    });
  };

  /**
   * Clear selected customer
   */
  const clearSelectedCustomer = () => {
    setSelectedCustomer(null);
    localStorage.removeItem("cia_selected_customer_id");
  };

  /**
   * Clear error
   */
  const clearError = () => {
    setError(null);
  };

  const value = {
    selectedCustomer,
    accessibleCustomers,
    loading,
    error,
    selectCustomer,
    switchCustomer,
    loadAccessibleCustomers,
    addAccessibleCustomer,
    getPrimaryCustomer,
    validateAccess,
    clearSelectedCustomer,
    clearError,
    hasSelectedCustomer: !!selectedCustomer,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext;
